import { Box, Flex, Text, useToken } from '@chakra-ui/react'
import { useMemo } from 'react'
import type { TooltipContentProps } from 'recharts'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import { useIsDarkMode } from '../hooks/useIsDarkMode'
import { useReportingI18n } from '../i18n'
import type { NumberData } from '../types'
import { formatDuration } from '../utils/formatter'
import NumberComparisonChartBatch from './NumberComparisonChartBatch'

interface NumberComparisonChartProps {
  data: NumberData[]
}

const BATCH_SIZE = 50

const NumberComparisonChart = ({ data }: NumberComparisonChartProps) => {
  const { text } = useReportingI18n()

  const isDark = useIsDarkMode()

  const barColors = useToken('colors', [
    'blue.400',
    'orange.400',
    'pink.400',
    'green.400',
    'purple.400',
  ])

  const lightColors = useToken('colors', [
    'gray.500',
    'gray.400',
    'gray.700',
    'gray.400',
    'gray.300',
    'gray.700',
  ])

  const darkColors = useToken('colors', [
    'gray.400',
    'gray.600',
    'gray.300',
    'gray.600',
    'gray.700',
    'gray.300',
  ])

  const [xTickFill, xStroke, yTickFill, yStroke, gridStroke, labelFill] = isDark
    ? darkColors
    : lightColors

  const chartColors = useMemo(
    () => ({ xTickFill, xStroke, yTickFill, yStroke, gridStroke, labelFill }),
    [xTickFill, xStroke, yTickFill, yStroke, gridStroke, labelFill],
  )

  const batches = useMemo(() => {
    const result: NumberData[][] = []
    for (let i = 0; i < data.length; i += BATCH_SIZE) {
      result.push(data.slice(i, i + BATCH_SIZE))
    }
    return result
  }, [data])

  const maxValue = useMemo(() => {
    if (data.length === 0) return 100
    return Math.max(...data.map((item) => item.inboundCalls))
  }, [data])

  const CustomTooltip = useMemo(
    () =>
      ({ active, payload }: TooltipContentProps) => {
        if (active && payload && payload.length) {
          const item = payload[0].payload as NumberData
          return (
            <Flex
              direction="column"
              bg="bg.default"
              border="1px solid"
              borderColor="border.default"
              borderRadius="4px"
              p="8px"
              boxShadow="md"
            >
              <Flex
                {...textStyles[textStyleKeys.Body1_Bold]}
                color="fg.default"
                mb="4px"
              >
                {item.name}
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                color="fg.muted"
                mb="4px"
              >
                {item.displayNumber}
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                justifyContent="space-between"
                width="100%"
              >
                <Text color="fg.default">
                  {text('numberComparisonChart.inboundCalls', 'Inbound Calls')}:
                </Text>
                <Text ml="8px" color="fg.info">
                  {typeof payload[0].value === 'number'
                    ? payload[0].value.toLocaleString()
                    : String(payload[0].value ?? '')}
                </Text>
              </Flex>
              <Flex
                {...textStyles[textStyleKeys.Body1]}
                justifyContent="space-between"
                width="100%"
              >
                <Text color="fg.default">
                  {text('numberComparisonChart.avgDuration', 'Avg Duration')}:
                </Text>
                <Text ml="8px" color="fg.muted">
                  {formatDuration(item.averageDuration)}
                </Text>
              </Flex>
            </Flex>
          )
        }
        return null
      },
    [text],
  )

  return (
    <Box width="100%">
      {batches.map((batch, batchIndex) => (
        <NumberComparisonChartBatch
          key={`batch-${batchIndex}`}
          data={batch}
          startIndex={batchIndex * BATCH_SIZE}
          barColors={barColors}
          chartColors={chartColors}
          CustomTooltip={CustomTooltip}
          isLast={batchIndex === batches.length - 1}
          isFirst={batchIndex === 0}
          maxValue={maxValue}
        />
      ))}
    </Box>
  )
}

export default NumberComparisonChart
